import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Autocomplete,
  IconButton,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  SwapHoriz as StatusIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { canDelete, canAccessRelatorios, ROLES } from '../../utils/permissions';

const STATUS_LIST = [
  { value: 'aberto', label: 'Aberto', color: 'primary' },
  { value: 'aguardando_banco', label: 'Aguardando Banco', color: 'warning' },
  { value: 'aguardando_retorno', label: 'Aguardando Retorno', color: 'info' },
  { value: 'fechado', label: 'Fechado', color: 'success' },
  { value: 'cancelado', label: 'Cancelado', color: 'error' },
];

const getStatusInfo = (status) => STATUS_LIST.find((s) => s.value === status) || { label: status, color: 'default' };

const STATUS_FINAIS = ['fechado', 'cancelado'];

export default function Reservas() {
  const { usuario } = useAuth();
  const [reservas, setReservas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tab, setTab] = useState(0);

  // Dialog nova/editar prospecção
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(null);

  // Dialog mudança de status
  const [openStatus, setOpenStatus] = useState(false);
  const [statusProspeccao, setStatusProspeccao] = useState(null);
  const [novoStatus, setNovoStatus] = useState('');
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  // Dialog novo cliente
  const [openNovoCliente, setOpenNovoCliente] = useState(false);

  // Relatório
  const [relatorio, setRelatorio] = useState(null);
  const [filtroRelatorio, setFiltroRelatorio] = useState({ data_inicio: '', data_fim: '', vendedor_id: '' });
  const [vendedores, setVendedores] = useState([]);

  const [formData, setFormData] = useState({
    cliente_id: null,
    veiculo_desejado: '',
    ano_desejado: '',
    data_reserva: new Date().toISOString().split('T')[0],
    valor_sinal: '',
    data_prevista_finalizacao: '',
    observacoes: '',
  });

  const [novoClienteData, setNovoClienteData] = useState({ nome: '', cpf_cnpj: '', telefone_principal: '', email: '' });

  const podeExcluir = canDelete(usuario);
  const podeVerRelatorios = canAccessRelatorios(usuario);
  const isGerente = usuario?.nivel_acesso === ROLES.GERENTE || usuario?.nivel_acesso === ROLES.ADMIN;

  useEffect(() => {
    loadReservas();
    loadClientes();
    if (isGerente) loadVendedores();
  }, []);

  const getApiErrorMessage = (error, fallback) => {
    try {
      const errors = error.response?.data?.errors;
      if (Array.isArray(errors) && errors.length > 0) return errors[0].msg || errors[0].message || fallback;
      return error.response?.data?.error || fallback;
    } catch {
      return fallback;
    }
  };

  const loadReservas = async () => {
    try {
      const response = await api.get('/reservas');
      setReservas(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar prospecções');
    }
  };

  const loadClientes = async () => {
    try {
      const response = await api.get('/clientes', { params: { limit: 1000 } });
      const data = response.data.clientes || response.data || [];
      setClientes(Array.isArray(data) ? data : []);
    } catch {
      setClientes([]);
    }
  };

  const loadVendedores = async () => {
    try {
      const response = await api.get('/usuarios');
      const lista = response.data.usuarios || response.data || [];
      setVendedores(Array.isArray(lista) ? lista.filter((u) => ['vendedor', 'gerente', 'admin'].includes(u.nivel_acesso)) : []);
    } catch {
      setVendedores([]);
    }
  };

  const loadRelatorio = async () => {
    try {
      const params = {};
      if (filtroRelatorio.data_inicio) params.data_inicio = filtroRelatorio.data_inicio;
      if (filtroRelatorio.data_fim) params.data_fim = filtroRelatorio.data_fim;
      if (filtroRelatorio.vendedor_id) params.vendedor_id = filtroRelatorio.vendedor_id;
      const response = await api.get('/reservas/relatorio', { params });
      setRelatorio(response.data);
    } catch {
      toast.error('Erro ao gerar relatório');
    }
  };

  // ── Nova / Editar prospecção ──────────────────────────────────────────────

  const handleOpen = (reserva = null) => {
    if (reserva) {
      setEditingId(reserva.id);
      setFormData({
        cliente_id: reserva.cliente_id,
        veiculo_desejado: reserva.veiculo_desejado || '',
        ano_desejado: reserva.ano_desejado || '',
        data_reserva: reserva.data_reserva.split('T')[0],
        valor_sinal: reserva.valor_sinal || '',
        data_prevista_finalizacao: reserva.data_prevista_finalizacao
          ? reserva.data_prevista_finalizacao.split('T')[0]
          : '',
        observacoes: reserva.observacoes || '',
      });
      setSelectedCliente(clientes.find((c) => c.id === reserva.cliente_id) || null);
    } else {
      setEditingId(null);
      setFormData({
        cliente_id: null,
        veiculo_desejado: '',
        ano_desejado: '',
        data_reserva: new Date().toISOString().split('T')[0],
        valor_sinal: '',
        data_prevista_finalizacao: '',
        observacoes: '',
      });
      setSelectedCliente(null);
    }
    setOpen(true);
  };

  const handleClose = () => { setOpen(false); setEditingId(null); setSelectedCliente(null); };

  const handleSubmit = async () => {
    if (!formData.cliente_id) { toast.error('Selecione um cliente'); return; }
    if (!formData.veiculo_desejado) { toast.error('Informe o veículo desejado'); return; }
    try {
      const dataToSend = { ...formData, valor_sinal: parseFloat(formData.valor_sinal) || 0 };
      if (editingId) {
        await api.put(`/reservas/${editingId}`, dataToSend);
        toast.success('Prospecção atualizada com sucesso!');
      } else {
        await api.post('/reservas', dataToSend);
        toast.success('Prospecção criada com sucesso!');
      }
      handleClose();
      loadReservas();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao salvar prospecção'));
    }
  };

  // ── Mudança de status ─────────────────────────────────────────────────────

  const handleOpenStatus = (reserva) => {
    setStatusProspeccao(reserva);
    setNovoStatus(reserva.status);
    setMotivoCancelamento('');
    setOpenStatus(true);
  };

  const handleCloseStatus = () => { setOpenStatus(false); setStatusProspeccao(null); setMotivoCancelamento(''); };

  const handleSalvarStatus = async () => {
    if (!novoStatus) { toast.error('Selecione um status'); return; }
    if (novoStatus === 'cancelado' && !motivoCancelamento.trim()) {
      toast.error('Informe o motivo do cancelamento');
      return;
    }
    try {
      const body = { status: novoStatus };
      if (novoStatus === 'cancelado') body.motivo_cancelamento = motivoCancelamento;
      await api.put(`/reservas/${statusProspeccao.id}`, body);
      toast.success('Status atualizado com sucesso!');
      handleCloseStatus();
      loadReservas();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao atualizar status'));
    }
  };

  // ── Excluir (gerente/admin) ───────────────────────────────────────────────

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta prospecção?')) return;
    try {
      await api.delete(`/reservas/${id}`);
      toast.success('Prospecção excluída com sucesso!');
      loadReservas();
    } catch (error) {
      toast.error('Erro ao excluir prospecção');
    }
  };

  // ── Novo cliente rápido ───────────────────────────────────────────────────

  const handleOpenNovoCliente = () => {
    setNovoClienteData({ nome: '', cpf_cnpj: '', telefone_principal: '', email: '' });
    setOpenNovoCliente(true);
  };

  const handleSubmitNovoCliente = async () => {
    if (!novoClienteData.nome || !novoClienteData.cpf_cnpj || !novoClienteData.telefone_principal) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      const response = await api.post('/clientes', { ...novoClienteData, ativo: true });
      toast.success('Cliente cadastrado com sucesso!');
      setOpenNovoCliente(false);
      await loadClientes();
      setTimeout(() => {
        const novoCliente = clientes.find((c) => c.id === response.data.id) || response.data;
        setSelectedCliente(novoCliente);
        setFormData((prev) => ({ ...prev, cliente_id: response.data.id }));
      }, 100);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao cadastrar cliente'));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Prospecções</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Nova Prospecção
        </Button>
      </Box>

      {podeVerRelatorios && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Prospecções" />
          <Tab label="Relatórios" icon={<ReportIcon />} iconPosition="start" />
        </Tabs>
      )}

      {/* ── ABA PROSPECÇÕES ── */}
      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Veículo Desejado</TableCell>
                <TableCell>Ano</TableCell>
                {isGerente && <TableCell>Vendedor</TableCell>}
                <TableCell>Data</TableCell>
                <TableCell>Valor Sinal</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reservas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isGerente ? 8 : 7} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Nenhuma prospecção cadastrada
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reservas.map((reserva) => {
                  const si = getStatusInfo(reserva.status);
                  const finalizado = STATUS_FINAIS.includes(reserva.status);
                  return (
                    <TableRow key={reserva.id}>
                      <TableCell>{reserva.cliente?.nome || '-'}</TableCell>
                      <TableCell>{reserva.veiculo_desejado || '-'}</TableCell>
                      <TableCell>{reserva.ano_desejado || '-'}</TableCell>
                      {isGerente && <TableCell>{reserva.vendedor?.nome || '-'}</TableCell>}
                      <TableCell>{new Date(reserva.data_reserva).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>R$ {parseFloat(reserva.valor_sinal || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip label={si.label} color={si.color} size="small" />
                        {reserva.status === 'cancelado' && reserva.motivo_cancelamento && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {reserva.motivo_cancelamento}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!finalizado && (
                          <>
                            <IconButton size="small" color="primary" onClick={() => handleOpen(reserva)} title="Editar">
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" color="warning" onClick={() => handleOpenStatus(reserva)} title="Alterar Status">
                              <StatusIcon />
                            </IconButton>
                          </>
                        )}
                        {podeExcluir && (
                          <IconButton size="small" color="error" onClick={() => handleDelete(reserva.id)} title="Excluir">
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── ABA RELATÓRIOS ── */}
      {tab === 1 && podeVerRelatorios && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Filtros</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="Data Início" type="date" InputLabelProps={{ shrink: true }}
                  value={filtroRelatorio.data_inicio}
                  onChange={(e) => setFiltroRelatorio({ ...filtroRelatorio, data_inicio: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="Data Fim" type="date" InputLabelProps={{ shrink: true }}
                  value={filtroRelatorio.data_fim}
                  onChange={(e) => setFiltroRelatorio({ ...filtroRelatorio, data_fim: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Vendedor</InputLabel>
                  <Select
                    value={filtroRelatorio.vendedor_id}
                    label="Vendedor"
                    onChange={(e) => setFiltroRelatorio({ ...filtroRelatorio, vendedor_id: e.target.value })}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {vendedores.map((v) => (
                      <MenuItem key={v.id} value={v.id}>{v.nome}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button variant="contained" fullWidth onClick={loadRelatorio} startIcon={<ReportIcon />}>
                  Gerar Relatório
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {relatorio && (
            <>
              {/* Cards de resumo por status */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={2}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4">{relatorio.total}</Typography>
                      <Typography variant="body2" color="text.secondary">Total</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {STATUS_LIST.map((s) => (
                  <Grid item xs={6} sm={2} key={s.value}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4">{relatorio.porStatus[s.value] || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Tabela por vendedor */}
              <Paper sx={{ mb: 3 }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6">Por Vendedor</Typography>
                </Box>
                <Divider />
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendedor</TableCell>
                      <TableCell align="center">Total</TableCell>
                      {STATUS_LIST.map((s) => (
                        <TableCell align="center" key={s.value}>{s.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {relatorio.porVendedor.map((v, i) => (
                      <TableRow key={i}>
                        <TableCell>{v.nome}</TableCell>
                        <TableCell align="center"><strong>{v.total}</strong></TableCell>
                        {STATUS_LIST.map((s) => (
                          <TableCell align="center" key={s.value}>{v.porStatus[s.value] || 0}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              {/* Listagem detalhada */}
              <Paper>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6">Registros Detalhados</Typography>
                </Box>
                <Divider />
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Vendedor</TableCell>
                      <TableCell>Veículo</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Motivo Cancelamento</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {relatorio.registros.map((r) => {
                      const si = getStatusInfo(r.status);
                      return (
                        <TableRow key={r.id}>
                          <TableCell>{new Date(r.created_at).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>{r.cliente?.nome || '-'}</TableCell>
                          <TableCell>{r.vendedor?.nome || '-'}</TableCell>
                          <TableCell>{r.veiculo_desejado || '-'}</TableCell>
                          <TableCell>
                            <Chip label={si.label} color={si.color} size="small" />
                          </TableCell>
                          <TableCell>{r.motivo_cancelamento || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}
        </Box>
      )}

      {/* ── DIALOG: Nova / Editar Prospecção ── */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Editar Prospecção' : 'Nova Prospecção'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={10}>
              <Autocomplete
                options={clientes}
                getOptionLabel={(option) => `${option.nome} - ${option.cpf_cnpj}`}
                value={selectedCliente}
                onChange={(_, newValue) => {
                  setSelectedCliente(newValue);
                  setFormData({ ...formData, cliente_id: newValue?.id || null });
                }}
                renderInput={(params) => <TextField {...params} label="Cliente *" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button variant="outlined" fullWidth startIcon={<PersonAddIcon />} onClick={handleOpenNovoCliente} sx={{ height: '56px' }}>
                Novo
              </Button>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth required label="Veículo Desejado"
                value={formData.veiculo_desejado}
                onChange={(e) => setFormData({ ...formData, veiculo_desejado: e.target.value })}
                placeholder="Ex: Fiat Uno, Honda Civic..."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Ano Desejado"
                value={formData.ano_desejado}
                onChange={(e) => setFormData({ ...formData, ano_desejado: e.target.value })}
                placeholder="Ex: 2020, 2021/2022..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Data da Prospecção" type="date"
                value={formData.data_reserva}
                onChange={(e) => setFormData({ ...formData, data_reserva: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Valor do Sinal (R$)" type="number"
                value={formData.valor_sinal}
                onChange={(e) => setFormData({ ...formData, valor_sinal: e.target.value })}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Data Prevista para Finalização" type="date"
                value={formData.data_prevista_finalizacao}
                onChange={(e) => setFormData({ ...formData, data_prevista_finalizacao: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Observações" multiline rows={3}
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Informações adicionais sobre a prospecção..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Atualizar' : 'Criar Prospecção'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG: Alterar Status ── */}
      <Dialog open={openStatus} onClose={handleCloseStatus} maxWidth="sm" fullWidth>
        <DialogTitle>Alterar Status da Prospecção</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {statusProspeccao && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Cliente: <strong>{statusProspeccao.cliente?.nome}</strong>
                  {' — '}{statusProspeccao.veiculo_desejado}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Novo Status</InputLabel>
                <Select
                  value={novoStatus}
                  label="Novo Status"
                  onChange={(e) => { setNovoStatus(e.target.value); setMotivoCancelamento(''); }}
                >
                  {STATUS_LIST.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {novoStatus === 'cancelado' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth required
                  label="Motivo do Cancelamento"
                  multiline rows={3}
                  value={motivoCancelamento}
                  onChange={(e) => setMotivoCancelamento(e.target.value)}
                  placeholder="Descreva o motivo do cancelamento..."
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatus}>Cancelar</Button>
          <Button onClick={handleSalvarStatus} variant="contained">Salvar Status</Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG: Novo Cliente Rápido ── */}
      <Dialog open={openNovoCliente} onClose={() => setOpenNovoCliente(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cadastro Rápido de Cliente</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Nome Completo"
                value={novoClienteData.nome}
                onChange={(e) => setNovoClienteData({ ...novoClienteData, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="CPF/CNPJ"
                value={novoClienteData.cpf_cnpj}
                onChange={(e) => setNovoClienteData({ ...novoClienteData, cpf_cnpj: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Telefone Principal"
                value={novoClienteData.telefone_principal}
                onChange={(e) => setNovoClienteData({ ...novoClienteData, telefone_principal: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Email" type="email"
                value={novoClienteData.email}
                onChange={(e) => setNovoClienteData({ ...novoClienteData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                Cadastro rápido. Para mais informações (endereço, RG, etc.), acesse a tela de Clientes.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNovoCliente(false)}>Cancelar</Button>
          <Button onClick={handleSubmitNovoCliente} variant="contained">Cadastrar Cliente</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
